import React from 'react';
import { format } from 'date-fns';
import { parseLocalDate } from '../../lib/utils';

// Helper to format hours to display
const formatHours = (hours) => {
  if (!hours || hours === 0) return '—';
  const totalMinutes = Math.round(hours * 60);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
};

export default function TimesheetEntries({ entries }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="mb-3 p-4 bg-slate-50 rounded text-center text-slate-500 text-sm">
        No time entries for this period
      </div>
    );
  }

  // Calculate totals - values are stored in hours (decimal)
  const totalHours = entries.reduce((sum, e) => {
    const travel_to = e.travel_to_job || 0;
    const on_job = e.time_on_job || 0;
    const travel_from = e.travel_from_job || 0;
    return sum + travel_to + on_job + travel_from;
  }, 0);

  return (
    <div className="mb-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Time Entries</p>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-100">
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Date</th>
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Job #</th>
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Client</th>
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Job Title</th>
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Contact</th>
            <th className="text-center py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase w-12">Team</th>
            <th className="text-right py-2 px-1 font-semibold text-slate-700 text-[10px] uppercase w-14">Travel To</th>
            <th className="text-right py-2 px-1 font-semibold text-slate-700 text-[10px] uppercase w-14">On Job</th>
            <th className="text-right py-2 px-1 font-semibold text-slate-700 text-[10px] uppercase w-14">Travel From</th>
            <th className="text-right py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase w-14">Total</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => {
            // Values are stored in hours (decimal format)
            const travel_to = entry.travel_to_job || 0;
            const on_job = entry.time_on_job || 0;
            const travel_from = entry.travel_from_job || 0;
            const entryTotal = travel_to + on_job + travel_from;
            
            return (
              <tr key={entry.id || idx} className="border-b border-slate-100">
                <td className="py-1.5 px-2 text-slate-700 text-[11px]">
                  {entry.date ? format(parseLocalDate(entry.date), 'EEE, MMM d') : '—'}
                </td>
                <td className="py-1.5 px-2 font-medium text-amber-600 text-[11px]">
                  {entry.job_number || '—'}
                </td>
                <td className="py-1.5 px-2 text-slate-600 text-[11px] max-w-[80px] truncate">
                  {entry.client_name || '—'}
                </td>
                <td className="py-1.5 px-2 text-slate-600 text-[11px] max-w-[100px] truncate">
                  {entry.job_title || '—'}
                </td>
                <td className="py-1.5 px-2 text-slate-500 text-[11px] max-w-[70px] truncate">
                  {entry.contact_name || '—'}
                </td>
                <td className="py-1.5 px-2 text-center text-slate-500 text-[10px] font-mono">
                  {entry.team_initials || '—'}
                </td>
                <td className="py-1.5 px-1 text-right text-slate-600 text-[11px]">
                  {formatHours(travel_to)}
                </td>
                <td className="py-1.5 px-1 text-right text-slate-600 text-[11px]">
                  {formatHours(on_job)}
                </td>
                <td className="py-1.5 px-1 text-right text-slate-600 text-[11px]">
                  {formatHours(travel_from)}
                </td>
                <td className="py-1.5 px-2 text-right font-medium text-slate-800 text-[11px]">
                  {formatHours(entryTotal)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-amber-50">
            <td colSpan={9} className="py-2 px-2 text-right font-bold text-slate-900 text-xs uppercase">Week Total</td>
            <td className="py-2 px-2 text-right font-bold text-slate-900 text-sm">{formatHours(totalHours)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
